<template>
  <div style="width: 100%;">
    <v-container fluid>
      <v-row>
        <v-col class="d-flex flex-row align-center">
          <v-btn text class="mx-2" @click="() => { this.$router.push({ path: '/expenses' }) }">
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          <h1>Expense <span v-if="expense.id">#{{ expense.id }}</span></h1>
          <v-chip v-if="expense.id" class="ml-3" :color="parseExpenseStatusColor(expense.status)">
            {{ parseExpenseStatus(expense.status) }}
          </v-chip>
          <span class="ml-4 d-flex flex-row align-center" style="gap: 6px;">
            <div :style="`border-radius: 50%; width: 10px; height: 10px; background-color: ${saveIndicatorColor};`"/>
            <span class="caption grey--text">{{ saveIndicatorText }}</span>
          </span>
          <v-progress-circular indeterminate color="green" v-if="loader" style="margin-left: 16px;" size="20"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="d-flex flex-column">
          <span v-if="expense.createdAt" class="caption grey--text">
            Created By {{ lookupUsername(expense.createdBy) }} at {{ utils.formatDate(expense.createdAt, 'withTime') }}
          </span>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="4" class="d-flex flex-row align-center" style="gap: 10px;">
          <v-select
            :items="expenseStatuses"
            item-text="label"
            item-value="value"
            :value="expense.status"
            outlined
            dense
            hide-details
            label="Status"
            :disabled="statusLocked"
            :color="parseExpenseStatusColor(expense.status)"
            @change="onStatusChange"
          >
            <template v-slot:item="{ item }">
              <v-chip small :color="item.color" class="mr-2" label>{{ item.label }}</v-chip>
            </template>
            <template v-slot:selection="{ item }">
              <v-chip small :color="item.color" label>{{ item.label }}</v-chip>
            </template>
          </v-select>
          <v-btn
            v-if="expense.id && statusLog.length > 0"
            outlined
            small
            @click="statusLogDialog = true"
          >
            <v-icon small class="mr-1">mdi-history</v-icon>
            Log ({{ statusLog.length }})
          </v-btn>
        </v-col>
      </v-row>

      <v-row v-if="!loader">
        <!-- Main Form -->
        <v-col cols="12" md="8">

          <!-- Name -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold pb-0">Expense Name</v-card-title>
            <v-card-text>
              <v-text-field
                label="Name *"
                v-model="expense.name"
                outlined
                dense
                placeholder="e.g. Office Rent, Electricity Bill"
                hide-details
              />
            </v-card-text>
          </v-card>

          <!-- Payment -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold pb-0">Payment</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Amount *"
                    v-model="expense.amount"
                    outlined
                    dense
                    type="number"
                    min="0"
                    prefix="$"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="expense.paymentDate"
                        label="Payment Date"
                        outlined
                        dense
                        readonly
                        clearable
                        prepend-inner-icon="mdi-calendar"
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="expense.paymentDate = null"
                      />
                    </template>
                    <v-date-picker v-model="expense.paymentDate" @input="dateMenu = false"/>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    :items="supplierItems"
                    v-model="expense.supplierId"
                    item-text="name"
                    item-value="id"
                    :search-input.sync="supplierSearch"
                    outlined
                    dense
                    clearable
                    label="Paid To"
                    no-data-text="No suppliers found"
                    @change="onSupplierChange"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content v-if="item.id === '__new__'">
                        <v-list-item-title class="primary--text">
                          <v-icon small color="primary" class="mr-1">mdi-plus-circle</v-icon>
                          {{ item.name }}
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-content v-else>
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    :items="expenseAccounts"
                    v-model="expense.accountId"
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    clearable
                    label="From Account"
                    no-data-text="No accounts found"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    label="Invoice Number"
                    v-model="expense.invoiceNumber"
                    outlined
                    dense
                    clearable
                    placeholder="e.g. INV-00123"
                    hide-details
                  />
                </v-col>
                <v-col cols="12">
                  <div class="caption grey--text mb-2">Payment Method</div>
                  <v-radio-group
                    v-model="expense.paymentMethodId"
                    row
                    dense
                    class="mt-0"
                    hide-details
                  >
                    <v-radio
                      v-for="method in paymentMethods"
                      :key="method.id"
                      :label="method.name"
                      :value="method.id"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Classification -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold pb-0">Classification</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    :items="categories"
                    v-model="expense.categoryId"
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    clearable
                    label="Category"
                  />
                </v-col>
                <v-col cols="12" sm="6" class="d-flex flex-column justify-center">
                  <v-switch
                    v-model="expense.isRecurring"
                    :label="expense.isRecurring ? 'Recurring' : 'One-Time'"
                    color="primary"
                    dense
                    inset
                    hide-details
                  />
                </v-col>
                <template v-if="expense.isRecurring">
                  <v-col cols="12" sm="6">
                    <v-select
                      :items="frequencyOptions"
                      v-model="expense.recurringFrequency"
                      outlined
                      dense
                      label="Frequency"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-menu v-model="endDateMenu" :close-on-content-click="false" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="expense.endDate"
                          label="End Date (optional)"
                          outlined
                          dense
                          readonly
                          clearable
                          prepend-inner-icon="mdi-calendar"
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="expense.endDate = null"
                        />
                      </template>
                      <v-date-picker v-model="expense.endDate" @input="endDateMenu = false"/>
                    </v-menu>
                  </v-col>
                </template>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Notes -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold pb-0">Notes</v-card-title>
            <v-card-text>
              <v-textarea
                v-model="expense.notes"
                outlined
                dense
                rows="3"
                placeholder="Any additional notes about this expense..."
                hide-details
              />
            </v-card-text>
          </v-card>

          <!-- Attachments -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold pb-0">Attachments</v-card-title>
            <v-card-text>
              <div v-if="attachments.length > 0" class="mb-3">
                <div
                  v-for="(att, i) in attachments"
                  :key="i"
                  class="d-flex flex-row align-center mb-1"
                  style="gap: 8px;"
                >
                  <v-icon small>mdi-paperclip</v-icon>
                  <a :href="`${getEndpoint}/expenses/attachments/${att.url.split('/').pop()}`" target="_blank">{{ att.name || `Attachment ${i + 1}` }}</a>
                </div>
              </div>
              <fileDropper
                v-if="expense.id"
                :uploadPoint="`${getEndpoint}/api/expenses/${expense.id}/attachments`"
                @cb="onAttachmentUploaded"
              />
            </v-card-text>
          </v-card>

        </v-col>

        <!-- Summary -->
        <v-col cols="12" md="4">
          <v-card outlined style="position: sticky; top: 16px;">
            <v-card-title class="subtitle-1 font-weight-bold">Summary</v-card-title>
            <v-card-text>
              <div class="d-flex flex-column" style="gap: 10px;">
                <div v-if="expense.invoiceId" class="d-flex flex-row justify-space-between">
                  <span class="grey--text">Linked PO</span>
                  <router-link :to="`/purchaseorders/view/${expense.invoiceId}`">#{{ expense.invoiceId }}</router-link>
                </div>
                <div v-if="expense.invoiceNumber" class="d-flex flex-row justify-space-between">
                  <span class="grey--text">Invoice #</span>
                  <span>{{ expense.invoiceNumber }}</span>
                </div>
                <div class="d-flex flex-row justify-space-between">
                  <span class="grey--text">Paid To</span>
                  <span>{{ selectedSupplier ? selectedSupplier.name : '-' }}</span>
                </div>
                <div class="d-flex flex-row justify-space-between">
                  <span class="grey--text">From Account</span>
                  <span>{{ selectedAccount ? selectedAccount.name : '-' }}</span>
                </div>
                <div class="d-flex flex-row justify-space-between">
                  <span class="grey--text">Category</span>
                  <span>{{ selectedCategory ? selectedCategory.name : '-' }}</span>
                </div>
                <div class="d-flex flex-row justify-space-between">
                  <span class="grey--text">Type</span>
                  <v-chip x-small :color="expense.isRecurring ? 'primary' : 'grey lighten-1'">
                    {{ expense.isRecurring ? 'Recurring' : 'One-Time' }}
                  </v-chip>
                </div>
                <div v-if="expense.isRecurring" class="d-flex flex-row justify-space-between">
                  <span class="grey--text">Frequency</span>
                  <span>{{ expense.recurringFrequency || '-' }}</span>
                </div>
                <div class="d-flex flex-row justify-space-between">
                  <span class="grey--text">Date</span>
                  <span>{{ expense.date ? utils.formatDate(expense.date, 'short') : '-' }}</span>
                </div>
                <div class="d-flex flex-row justify-space-between">
                  <span class="grey--text">Payment Method</span>
                  <span>{{ selectedPaymentMethod ? selectedPaymentMethod.name : '-' }}</span>
                </div>
                <v-divider/>
                <div class="d-flex flex-row justify-space-between align-center">
                  <span class="text-h6">Amount</span>
                  <span class="text-h5 font-weight-bold">{{ utils.formatCurrency(expense.amount || 0) }}</span>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="statusDialog.open" max-width="490">
      <v-card>
        <v-card-title class="text-h5">Change status to "{{ parseExpenseStatus(statusDialog.pending) }}"?</v-card-title>
        <v-card-text>
          {{ statusDialog.pending === -1 ? 'This expense will be voided and cannot be changed again.' : '' }}
          {{ statusDialog.pending === 4  ? 'This expense will be marked as Paid and cannot be changed again.' : '' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="info" text @click="cancelStatusChange">Cancel</v-btn>
          <v-btn :color="statusDialog.pending === -1 ? 'error' : 'success'" @click="applyStatusChange">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="statusLogDialog" max-width="560">
      <v-card>
        <v-card-title class="text-h6">Status Change Log</v-card-title>
        <v-card-text class="pa-0">
          <v-list dense>
            <v-list-item v-for="(entry, i) in statusLog" :key="i" class="py-2" style="border-bottom: 1px solid rgba(0,0,0,0.08);">
              <v-list-item-content>
                <v-list-item-title class="d-flex flex-row align-center" style="gap: 8px;">
                  <v-chip x-small :color="parseExpenseStatusColor(entry.from)" label>{{ parseExpenseStatus(entry.from) }}</v-chip>
                  <v-icon x-small>mdi-arrow-right</v-icon>
                  <v-chip x-small :color="parseExpenseStatusColor(entry.to)" label>{{ parseExpenseStatus(entry.to) }}</v-chip>
                </v-list-item-title>
                <v-list-item-subtitle class="mt-1">
                  {{ lookupUsername(entry.userId) }} &mdash; {{ utils.formatDate(entry.timestamp, 'withTime') }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="statusLogDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackObj.state" :timeout="3000" :color="snackObj.color">
      {{ snackObj.text }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snackObj.state = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
  import axios from 'axios';
  import { mapGetters } from 'vuex';
  import utils from '../../plugins/helpers';
  import fileDropper from '../../components/FileDropper.vue';

  const NEW_SUPPLIER_SENTINEL = '__new__';

  const DEFAULTS = {
    name: '',
    supplierId: null,
    accountId: null,
    invoiceId: null,
    invoiceNumber: '',
    amount: null,
    categoryId: null,
    paymentMethodId: null,
    isRecurring: false,
    recurringFrequency: null,
    date: null,
    endDate: null,
    notes: '',
    status: 0,
    attachments: [],
  };

  export default {
    components: { fileDropper },
    data() {
      return {
        utils,
        loader: false,
        dataLoaded: false,
        saveTimeout: null,
        updateStatus: 0,
        snackObj: { state: false, color: '', text: '' },
        expense: { ...DEFAULTS },
        categories: [],
        expenseAccounts: [],
        suppliers: [],
        supplierSearch: null,
        frequencyOptions: ['Daily', 'Weekly', 'Monthly', 'Yearly'],
        dateMenu: false,
        endDateMenu: false,
        statusDialog: { open: false, pending: null },
        statusLogDialog: false,
        expenseStatuses: [
          { value: -1, label: 'Voided',           color: 'error' },
          { value:  0, label: 'Draft',             color: 'grey' },
          { value:  1, label: 'Pending Approval',  color: 'info' },
          { value:  2, label: 'Approved',          color: 'primary' },
          { value:  3, label: 'Rejected',          color: 'warning' },
          { value:  4, label: 'Paid',              color: 'success' },
        ],
      };
    },
    computed: {
      ...mapGetters(['getEndpoint', 'lookupUsername', 'paymentMethods']),
      statusLocked() {
        return this.expense.status === -1 || this.expense.status === 4;
      },
      attachments() {
        return this.expense.metadata?.attachments || [];
      },
      statusLog() {
        return (this.expense.metadata && this.expense.metadata.statusLog) || [];
      },
      supplierItems() {
        const base = [...this.suppliers];
        const term = this.supplierSearch ? this.supplierSearch.trim() : '';
        if (term) base.push({ id: NEW_SUPPLIER_SENTINEL, name: `Add New Supplier: "${term}"` });
        return base;
      },
      selectedSupplier() {
        if (!this.expense.supplierId) return null;
        return this.suppliers.find(s => s.id === this.expense.supplierId) || null;
      },
      selectedCategory() {
        if (!this.expense.categoryId) return null;
        return this.categories.find(c => c.id === this.expense.categoryId) || null;
      },
      selectedAccount() {
        if (!this.expense.accountId) return null;
        return this.expenseAccounts.find(a => a.id === this.expense.accountId) || null;
      },
      selectedPaymentMethod() {
        if (!this.expense.paymentMethodId) return null;
        return this.paymentMethods.find(m => m.id === this.expense.paymentMethodId) || null;
      },
      saveIndicatorColor() {
        if (this.updateStatus === 2) return 'red';
        if (this.updateStatus === 1) return 'orange';
        return 'green';
      },
      saveIndicatorText() {
        if (this.updateStatus === 2) return 'Save failed';
        if (this.updateStatus === 1) return 'Saving...';
        return 'Saved';
      },
    },
    watch: {
      expense: {
        deep: true,
        handler() {
          if (!this.dataLoaded) return;
          if (this.saveTimeout) clearTimeout(this.saveTimeout);
          this.updateStatus = 1;
          this.saveTimeout = setTimeout(() => { this.saveExpense(); }, 600);
        },
      },
    },
    async mounted() {
      try {
        this.loader = true;
        if (this.$route.params.id) {
          let res = await axios.get(`${this.getEndpoint}/api/expenses/${this.$route.params.id}`);
          if (res.data.error) throw res.data.error;
          this.expense = { ...DEFAULTS, ...res.data.data };
        }
        let [catRes, suppRes, acctRes] = await Promise.all([
          axios.get(`${this.getEndpoint}/api/expensecategories`),
          axios.get(`${this.getEndpoint}/api/suppliers`),
          axios.get(`${this.getEndpoint}/api/expenseaccounts`),
        ]);
        if (catRes.data.error) throw catRes.data.error;
        if (suppRes.data.error) throw suppRes.data.error;
        if (acctRes.data.error) throw acctRes.data.error;
        this.categories = catRes.data.data;
        this.suppliers = suppRes.data.data;
        this.expenseAccounts = acctRes.data.data;
      } catch (error) {
        console.error(error);
        this.snack(error.msg || error.message || error, 'error');
      } finally {
        this.loader = false;
        this.$nextTick(() => { this.dataLoaded = true; });
      }
    },
    methods: {
      snack(text, color = '') {
        this.snackObj.text = text;
        this.snackObj.state = true;
        this.snackObj.color = color;
      },
      async onSupplierChange(val) {
        if (val !== NEW_SUPPLIER_SENTINEL) return;
        this.expense.supplierId = null;
        const name = this.supplierSearch ? this.supplierSearch.trim() : '';
        if (!name) return;
        try {
          let res = await axios.post(`${this.getEndpoint}/api/suppliers`, { name });
          if (res.data.error) throw res.data.error;
          const created = res.data.data;
          this.suppliers.push(created);
          this.expense.supplierId = created.id;
          this.snack(`Supplier "${created.name}" created.`, 'success');
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.message || error, 'error');
        }
      },
      async saveExpense() {
        if (this.expense.supplierId === NEW_SUPPLIER_SENTINEL) return;
        try {
          this.updateStatus = 1;
          let res = await axios.put(`${this.getEndpoint}/api/expenses/${this.expense.id}`, this.expense);
          if (res.data.error) throw res.data.error;
          let fresh = await axios.get(`${this.getEndpoint}/api/expenses/${this.expense.id}`);
          if (fresh.data.error) throw fresh.data.error;
          this.dataLoaded = false;
          this.expense = { ...DEFAULTS, ...fresh.data.data };
          this.$nextTick(() => { this.dataLoaded = true; });
          this.updateStatus = 0;
        } catch (error) {
          console.error(error);
          this.snack(error.msg || error.message || error, 'error');
          this.updateStatus = 2;
        }
      },
      async onAttachmentUploaded(success) {
        if (!success) { this.snack('Attachment upload failed.', 'error'); return; }
        try {
          let res = await axios.get(`${this.getEndpoint}/api/expenses/${this.expense.id}`);
          if (res.data.error) throw res.data.error;
          this.dataLoaded = false;
          this.expense = { ...DEFAULTS, ...res.data.data };
          this.$nextTick(() => { this.dataLoaded = true; });
        } catch (error) {
          console.error(error);
        }
      },
      onStatusChange(val) {
        if (val === -1 || val === 4) {
          this.statusDialog.pending = val;
          this.statusDialog.open = true;
        } else {
          this.expense.status = val;
        }
      },
      cancelStatusChange() {
        this.statusDialog.open = false;
        this.statusDialog.pending = null;
      },
      applyStatusChange() {
        this.expense.status = this.statusDialog.pending;
        this.statusDialog.open = false;
        this.statusDialog.pending = null;
      },
      parseExpenseStatus(status) {
        const found = this.expenseStatuses.find(s => s.value === status);
        return found ? found.label : '';
      },
      parseExpenseStatusColor(status) {
        const found = this.expenseStatuses.find(s => s.value === status);
        return found ? found.color : 'grey';
      },
    },
  };
</script>
